/*
 * Copyright (c) Nexign, JSC, 2022-2023.
 */
/**
 *
 * @author Ilya Babanov
 * @version 1.23.0
 * @since 1.22.0
 */

import type { IConnection } from '@pages/PageAccountInfo/types/pageAccountInfoGeneralTypes';
import { removeEmptyValuesFromObject } from '@utils/removeEmptyValues';

import { EEdmDeliveryInfoFields } from '../constants';
import { EdmOperatorId } from '../types';
import type {
  TEditEdmResponsibleForm,
  IEdmOperator,
  IAddEdmConnectionForm,
  IOperatorsOptions,
  IEdmOptionItem,
} from '../types';

export const mapEdmOperatorsToSelectOptions = (
  operators: IEdmOperator[],
): IOperatorsOptions => {
  const initialOperator = operators.find((operator) => !operator.isRoaming);

  const options: IEdmOptionItem[] = operators.map((operator) => {
    return {
      label: operator.name,
      value: EdmOperatorId(operator.edmOperatorId),
      isRoaming: operator.isRoaming,
    };
  });

  return {
    initialOption: {
      label: initialOperator?.name ?? '',
      value: initialOperator?.edmOperatorId
        ? EdmOperatorId(initialOperator?.edmOperatorId)
        : '',
      isRoaming: initialOperator?.isRoaming ?? false, // isRoaming используем для отображения формы ответственного
    },
    options,
  };
};

export const mapFormDataToAddEdmConnectionRequest = (
  data: IAddEdmConnectionForm,
): Partial<Record<string, unknown>> => {
  const { operator, email, name, phone } = data;

  if (!operator.isRoaming) {
    return { edmOperatorId: EdmOperatorId(Number(operator.value)) };
  }

  return removeEmptyValuesFromObject({
    edmOperatorId: EdmOperatorId(Number(operator.value)),
    email,
    personName: name,
    phoneNumber: phone,
  });
};

export const setEditResponsibleInitialFormValues = (
  connection: IConnection,
) => {
  const {
    allowEmailEdit,
    allowPersonNameEdit,
    allowPhoneNumberEdit,
    responsibleEmail,
    responsibleName,
    responsiblePhone,
  } = connection;

  const name = allowPersonNameEdit && responsibleName ? responsibleName : '';
  const email = allowEmailEdit && responsibleEmail ? responsibleEmail : '';
  const phone =
    allowPhoneNumberEdit && responsiblePhone ? responsiblePhone : '';

  return {
    [EEdmDeliveryInfoFields.name]: name,
    [EEdmDeliveryInfoFields.email]: email,
    [EEdmDeliveryInfoFields.phone]: phone,
  };
};

export const mapFormDataToUpdateEdmResponsibleRequest = (
  data: TEditEdmResponsibleForm,
): Partial<Record<string, unknown>> => {
  const { email, name, phone } = data;

  return removeEmptyValuesFromObject({
    email,
    personName: name,
    phoneNumber: phone,
  });
};

export const sortConnections = (connections: IConnection[]) =>
  [...connections].sort((prev, next) => {
    if (prev.statusId !== undefined && next.statusId !== undefined) {
      return prev.statusId - next.statusId < 0 ? 1 : -1;
    }
    return 0;
  });
